import { defineStore } from 'pinia'
import Vue, { ref, computed } from 'vue'
import { useAppStore } from 'Store/app'
import { useSettingsStore } from 'Store/settings'
import { CustomBot, Chat, Reservation, User } from 'src/apis'
import triplaLocalStorage from 'Utilities/triplaLocalStorage'
import { removeSession } from 'Utilities/Session'
import { EventBus } from 'ChatWidget/EventBus'
import {
  MAX_FILES_UPLOAD_AT_ONCE,
  supportedUploadImageTypes,
  supportedUploadDocTypes,
  UPLOAD_FILE_SIZE_LIMIT,
  LocalStorageKeys
} from 'javascript/Constants'
import i18n from 'javascript/i18n'
import { useUserStore } from 'Store/user'
import { useRestaurantStore } from 'Store/restaurant'
import PubNub from 'pubnub'
import { useLazyLoadStore } from 'Store/lazy-load'
import Push from 'push.js'
import {
  isMobile,
  camelCase,
  upperCamelCase,
  clearStoredBrandChat,
  clearStoredBranchChat,
  storeBrandHotelOnSwitch,
  storeBranchHotelOnSwitch
} from 'Utilities'
import { useBrandChatbotStore } from 'Store/brand-chatbot'
import { openBookingWidget } from 'Utilities/openBookingWidget'
import { useBottomPanelStore } from 'Store/bottom-panel'
import { useFormStore } from 'Store/form'
import { useDetailsPageStore } from 'Store/details-page'
import { appAccessInfo } from 'Utilities/appAccess'
import { useIconMessageStore } from 'Store/icon-message'
import callAnonymousSignIn from 'Utilities/anonymousSignIn'
import { useTermsConditionsStore } from 'Store/terms-conditions'
import PQueue from 'p-queue/dist'
import { useUserPanelStore } from 'Store/user-panel'
import { getChatIconImageFromSettings } from 'Utilities/helpers'
import { useColorsStore } from 'Store/colors'
import Cookies from 'js-cookie'
import { showLoginView } from 'javascript/helpers/login'

const MESSAGE_DELAY_DURATION = 1000

function retry(fn, check, duration, count) {
  let start = 0

  function tryIt() {
    if (start === count) return

    if (check()) {
      fn()
    } else {
      setTimeout(tryIt, duration)
      start += 1
    }
  }

  tryIt()
}

export const useChatStore = defineStore('Chat', () => {
  const appStore = useAppStore()
  const userStore = useUserStore()
  const settingsStore = useSettingsStore()
  const restaurantStore = useRestaurantStore()
  const lazyLoadStore = useLazyLoadStore()
  const brandChatbotStore = useBrandChatbotStore()
  const bottomPanelStore = useBottomPanelStore()
  const formStore = useFormStore()
  const detailsPageStore = useDetailsPageStore()
  const iconMessageStore = useIconMessageStore()
  const termsConditionsStore = useTermsConditionsStore()
  const colorsStore = useColorsStore()

  let typingTimeoutId
  let delayMessageTimeoutId
  let lastMessageNotifiedAt
  let lastConciergeMessagePublished
  const conciergeMessageQueue = []
  let loadingMessages = []
  let chatOpenEmailTrigger
  let lazyIconAutoMessage
  const refreshChatQueue = new PQueue({ concurrency: 1 })

  const pubnub = ref(null)
  const chat = ref(null)
  const messages = ref([])
  const isTyping = ref(false)
  const isServerResponding = ref(false)
  const loading = ref(false)
  const isMessageDelaying = ref(false)

  const chatId = computed(() => chat.value?.id)

  function setChat(newChat) {
    chat.value = newChat
  }

  function setLoading(value) {
    loading.value = value
  }

  // eslint-disable-next-line camelcase
  function initPubNub({ subscribe_key, publish_key, auth_key }) {
    pubnub.value = PubNub.init({
      subscribe_key,
      publish_key,
      auth_key,
      uuid: `user-${userStore.id}`,
      ssl: true
    })
  }

  function subscribeChat(chat) {
    return new Promise((resolve) => {
      pubnub.value.subscribe({
        channel: [chat.channel],
        message: handlePubNubMessage,
        connect: () => {
          resolve()
        }
      })
    })
  }

  function unsubscribeChat() {
    if (!pubnub.value) return Promise.resolve()

    return new Promise((resolve, reject) => {
      pubnub.value.unsubscribe({
        channel: pubnub.value.get_subscribed_channels(),
        callback: resolve,
        err: reject
      })
      pubnub.value = null
    })
  }

  function showTypingIndicator() {
    isTyping.value = true
    clearTimeout(typingTimeoutId)
    typingTimeoutId = setTimeout(() => {
      isTyping.value = false
    }, 20000)
  }

  function hideTypingIndicator() {
    clearTimeout(typingTimeoutId)
    isTyping.value = false
  }

  function publishTyping(typingType) {
    if (!pubnub.value || !chat.value) return

    pubnub.value.publish({
      channel: chat.value.channel,
      message: { user: { typing: typingType } }
    })
  }

  function handleIncomingNotification(message) {
    // skip notification if the user is focusing on this page
    if (!document.hidden) return

    notifyNewMessage(message)
  }

  function notifyNewMessage(message) {
    if (isMobile()) return

    const sixtySeconds = 60 * 1000
    if (Date.now() - lastMessageNotifiedAt < sixtySeconds) return

    lastMessageNotifiedAt = Date.now()

    const url = new URL(window.location)
    url.searchParams.set('triplabot_open', true)
    const chatMessage = message.data.chat_message
    const notificationTag = `triplabot-message-${chatMessage.id}`
    Push.create(chatMessage.notification.title, {
      body: chatMessage.notification.content,
      tag: notificationTag,
      onClick() {
        if (!window.closed) {
          window.focus()
        } else {
          window.open(url.toString())
        }
        Push.close(notificationTag)
      }
    })
  }

  function enqueueConciergeMessage(message) {
    if (delayMessageTimeoutId) {
      conciergeMessageQueue.push(message)
      return
    }

    const messageType = message.message_rich && message.message_rich.type
    const isNonDelayMessage = ['quick_reply', 'language', 'user_evaluation'].includes(messageType)
    const delayDuration = isNonDelayMessage ? 0 : MESSAGE_DELAY_DURATION

    isMessageDelaying.value = true
    delayMessageTimeoutId = setTimeout(() => {
      pushChatMessage(message, true)
      isMessageDelaying.value = false
      delayMessageTimeoutId = null
      const msg = conciergeMessageQueue.shift()
      if (msg) {
        enqueueConciergeMessage(msg)
      }
    }, delayDuration)
  }

  function pushChatMessage(message, skipDelay = false) {
    const isConciergeMessage = !!message.concierge
    if (isConciergeMessage && !skipDelay) {
      const isTooEarly = Date.now() - lastConciergeMessagePublished < MESSAGE_DELAY_DURATION

      if (isTooEarly) {
        lastConciergeMessagePublished = Date.now()
        enqueueConciergeMessage(message)
        return
      } else {
        lastConciergeMessagePublished = Date.now()
      }
    }

    const isUserMessage = !!message.user
    if (isUserMessage) {
      // should not delay concierge message right after a user message
      lastConciergeMessagePublished = 0
    }

    isTyping.value = false
    if (message.total_message_parts || message.chat_message_part) {
      loadingMessages.push(message)
      const totalParts =
        message.total_message_parts || message.chat_message_part?.total_message_parts
      if (loadingMessages.length === totalParts) {
        addMessage(mergeLoadingMessages(loadingMessages))
        loadingMessages = []
      }
    } else {
      addMessage(message)
    }
  }

  function mergeLoadingMessages(messages) {
    const message = messages.find((m) => !m.chat_message_part)
    const messageParts = messages
      .filter((m) => m.chat_message_part)
      .sort((a, b) => a.chat_message_part.part_index - b.chat_message_part.part_index)
      .map((m) => m.chat_message_part.message_part)

    return {
      ...message,
      message: message.message + messageParts.join('')
    }
  }

  function addNotificationFromPubNub(message) {
    // If we get a notificaton message, we do not want to allow it if
    // it is within a thrshold.
    const threshold = -10000
    const lastNotification = brandChatbotStore.notificationTime
    const shouldNotify = lastNotification === 0 || lastNotification - Date.now() < threshold

    if (message && shouldNotify) {
      brandChatbotStore.setNotificationTime(Date.now())
      pushChatMessage(message)
    }
  }

  function initForm(page, chat) {
    if (!page) return
    if (['rental_car_form'].includes(page)) {
      Vue.rollbar.info(`rentalCarForm initForm ${chatId.value}`)
      bottomPanelStore.setPickupPrefectures(chat.message_rich.prefectures)
    }

    formStore.setActiveForm(camelCase(page))
    const maxKidAge = chat.message_rich.max_kid_age
    if (maxKidAge) bottomPanelStore.setMaxKidAge(maxKidAge)
    detailsPageStore.setPage(page)
    detailsPageStore.setPageChat(chat)
    formStore.setInitialContactForm(chat.message_rich.contact_form || {})
    formStore.setInitialRestaurantForm(chat.message_rich.restaurant_form || {})
    detailsPageStore.setDetailsPageShown(upperCamelCase(page))
  }

  function addMessageFromPubNub(message) {
    if (!message) return

    pushChatMessage(message)
    Vue.prototype.$gtag?.recordEventAutoMessage(message)

    message.is_from_pubnub = true

    if (message.message_rich && message.message_rich.type === 'form_action') {
      const actions = message.message_rich.actions
      const page = actions && actions[0] && actions[0].page
      initForm(page, message)
    } else if (message.message_rich && message.message_rich.type === 'booking_widget') {
      appStore.setAutoMessageAuthId(
        message.message_rich.auto_message_info?.successful_auth_auto_message_id || message.id
      )
      openBookingWidget(message.message_rich.booking_widget_params)
    } else if (
      message.message_rich &&
      message.message_rich.auto_message_info &&
      message.message_rich.auto_message_info.type === 'auth' &&
      message.message_rich.auto_message_info.show_widget_immediately
    ) {
      showLoginView()
      if (appStore.autoMessageAuthType !== 'icon_auto_message') {
        appStore.setAutoMessageAuthType('action_auto_message')
        appStore.setAutoMessageAuthId(
          message.message_rich.auto_message_info.successful_auth_auto_message_id || message.id
        )
      }
    }
  }

  function removeMessage(message) {
    const index = messages.value.findIndex((m) => m.id === message.id)
    messages.value.splice(index, 1, message)
  }

  function handlePubNubMessage(message) {
    if (message.oauth_callback || message.get_email) {
      EventBus.$emit('refresh-oauth-login', message)
      refreshOAuthLogin(message).then()
      return
    } else if (message.lazy_load) {
      lazyLoadStore.setLazyData(message.lazy_load)
    } else if (message.concierge && message.concierge.typing === 'start') {
      isServerResponding.value = true
      showTypingIndicator()
      return
    }

    // internal chat messages don't have object.data
    if (!message.data) return

    if (message.data.chat_message && message.data.chat_message.notification) {
      handleIncomingNotification(message)
    }

    if (
      message.data.chat_message &&
      message.data.chat_message.message_rich &&
      message.data.chat_message.message_rich.is_notification
    ) {
      isServerResponding.value = false
      hideTypingIndicator()
      addNotificationFromPubNub(message.data.chat_message)
    } else if (message.data.chat_message?.deleted_at) {
      removeMessage(message.data.chat_message)
    } else if (message.data.chat_message_part) {
      addMessageFromPubNub(message.data)
    } else {
      const chatMessage = message.data.chat_message
      if (chatMessage.hasOwnProperty('current_custom_bot_step_index')) {
        window.parent.postMessage(
          {
            type: 'current_custom_bot_step_index',
            value: chatMessage.current_custom_bot_step_index
          },
          '*'
        )
      }

      addMessageFromPubNub(chatMessage)
    }
  }

  function openSendEmail() {
    const urlQuery = window.location.search.substring(1)
    if (/triplabot_contact=2/.test(urlQuery)) chatOpenEmailTrigger = 'email'
  }

  function openSendPhone() {
    const urlQuery = window.location.search.substring(1)
    if (/triplabot_contact=1/.test(urlQuery)) {
      showAutoMessageOpen(settingsStore.getSettings.open_triplabot_message, '1')
    }
  }

  // method to trigger the non-phone auto messages on chat open
  function triggerChatOpen() {
    // if the boolean is true, means user opened chat using the special button
    // data-triplabot-open-message="2"
    if (chatOpenEmailTrigger) {
      Chat.chatOpenTrigger(chatId.value, chatOpenEmailTrigger)
      // set boolean to false so as not to always trigger on each refresh (unless user clicks button again)
      chatOpenEmailTrigger = false
    }
  }

  function showAutoMessageOpen(messages, trigger) {
    let triggerValue = 'other'
    // trigger = null -> standard
    // trigger = 1 -> phone
    // trigger = 2 -> email
    if (trigger === '1') {
      // messages is false if no phone messages
      if (!messages) return
      messages.forEach((message) => {
        // messages is an array of messages
        message.published_at = Date.now()
        pushChatMessage(message)
        Vue.prototype.$gtag?.recordEventAutoMessage(message)
      })
    } else {
      if (!trigger) {
        triggerValue = 'standard'
      } else if (trigger === '2') triggerValue = 'email'
      if (chat.value) {
        // chat already loaded, so we can trigger the auto messages
        Chat.chatOpenTrigger(chatId.value, triggerValue).then()
      } else {
        // chat not loaded yet, so set this boolean to trigger auto messages after chat loads
        chatOpenEmailTrigger = triggerValue
      }
    }
  }

  async function fetchChats() {
    if (
      !settingsStore.getSettings.bot_languages.includes(
        triplaLocalStorage.getItem(LocalStorageKeys.LOCALE)
      )
    ) {
      triplaLocalStorage.setItem(
        LocalStorageKeys.LOCALE,
        settingsStore.getSettings.default_language
      )
      i18n.locale = settingsStore.getSettings.default_language
    }
    loading.value = true
    await unsubscribeChat()
    removeChat()
    let newChat = chat.value

    if (!newChat || settingsStore.getFacilitySignIn) {
      if (settingsStore.isCustomBotTestMode) {
        const url = new URL(window.location)
        const customBotId = url.searchParams.get('custom_bot_id')
        const res = await CustomBot.test(settingsStore.getHotelId, customBotId)
        newChat = res.data.chat
      } else {
        const url = new URL(window.location)

        const autoMessageIds = url.searchParams.get('auto_messages')
        const payload = {
          code: appStore.websiteFacilityCode,
          auto_messages: autoMessageIds ? autoMessageIds.split(',') : undefined
        }
        if (window.__tripla_guide_website__) {
          payload.tripla_guide = { room_uuid: url.searchParams.get('room_id') }
        }
        const res = await Chat.GetChat(null, payload)
        newChat = res.data.chat
      }

      chat.value = newChat

      openSendEmail()
      openSendPhone()
      triggerChatOpen()

      if (lazyIconAutoMessage) {
        sendIconAutoMessage(lazyIconAutoMessage)
      }
    }

    initPubNub(chat.value)
    await subscribeChat(chat.value)
    await fetchMessages()

    if (settingsStore.isBrandFacility) {
      brandChatbotStore.fetchBranchCount(chatId.value)
    }
    if (appAccessInfo.restaurantId) {
      Reservation.startRestaurantReservation(appAccessInfo.restaurantId).then()
    }

    if (!Push.Permission.has()) {
      Push.Permission.request()
    }

    if (settingsStore.isCustomBotTestMode) {
      const message = [...messages.value]
        .reverse()
        .find((m) => m.hasOwnProperty('current_custom_bot_step_index'))
      if (message) {
        window.parent.postMessage(
          {
            type: 'current_custom_bot_step_index',
            value: message.current_custom_bot_step_index
          },
          '*'
        )
      }
    }

    loading.value = false
    EventBus.$emit('load-chats')
  }

  async function fetchMessages(currentChat = chat.value) {
    triplaLocalStorage.setItem('triplabot_chat_id', currentChat.id)

    const res = await Chat.GetChatMessages(currentChat.id, {
      custom_bot_test: settingsStore.isCustomBotTestMode
    }).catch(() => ({ error: true }))

    if (res.error) {
      // if any error hapenned here, remove current session and fetch another chat
      removeSession()
      refreshChat()
    }

    const existingMessageIds = messages.value.reduce(
      (acc, cur) => ({
        ...acc,
        [cur.id]: true
      }),
      {}
    )
    const appendedMessages = res.data.chat_messages.filter((cm) => !existingMessageIds[cm.id])
    const newMessages = messages.value.concat(appendedMessages)
    messages.value = newMessages

    if (newMessages.length > 0) {
      appStore.setLatestMessageID(newMessages[newMessages.length - 1].id)
    }

    return newMessages
  }

  function refreshChat() {
    refreshChatQueue.add(() => {
      return callAnonymousSignIn(settingsStore.getSettings)
        .then((json) => {
          if (json.data.user.known_user_signed_in && settingsStore.isTermsConditionsEnabled) {
            User.GetTermsConditionsStatus(settingsStore.getHotelId)
              .then((res) => {
                termsConditionsStore.setAccepted(res.data.accept_terms_and_conditions)
                return res.data.accept_terms_and_conditions || false
              })
              .then((isAccepted) => {
                if (isAccepted) return

                User.AcceptTermsConditions(settingsStore.getHotelId).then(() => {
                  termsConditionsStore.setAccepted(true)
                })
              })
          }

          if (!settingsStore.getFacilitySignIn) {
            userStore.refreshLogin(json)
          }
        })
        .then(fetchChats)
    })
  }

  function addMessage(message) {
    // initial chat auto messages might be duplicated due to racing condition
    // in BE so we need to check and filter them out
    if (
      message.message_rich &&
      message.message_rich.auto_message_info &&
      message.message_rich.auto_message_info.trigger === 'initial' &&
      messages.value.find((m) => m.id === message.id)
    )
      return

    messages.value = [...messages.value, message]
    appStore.setLatestMessageID(message.id)
  }

  async function sendFiles(files) {
    const errors = []
    const promises = []

    files.slice(0, MAX_FILES_UPLOAD_AT_ONCE).forEach((file, index) => {
      const fileType = file.type.startsWith('image')
        ? file.type.split('/').pop()
        : file.name.split('.').pop()
      const supportedFileTypes = [...supportedUploadImageTypes, ...supportedUploadDocTypes]

      if (!supportedFileTypes.includes(fileType.toLowerCase())) {
        errors.push({
          message: i18n.t('file_upload.unsupported_file_type', { file_type: fileType }),
          filename: file.name
        })
        return
      }

      if (file.size > UPLOAD_FILE_SIZE_LIMIT) {
        errors.push({
          message: i18n.t('file_upload.file_size_exceeded'),
          filename: file.name
        })
        return
      }

      promises.push(
        Chat.sendFile(
          file,
          index,
          supportedUploadImageTypes.includes(fileType) ? 'image' : undefined
        ).catch(() => {
          errors.push({
            message: i18n.t('file_upload.file_upload_failed'),
            filename: file.name
          })
        })
      )
    })

    await Promise.all(promises)

    errors.forEach((error) => {
      addMessage({
        user: { id: useUserStore().id },
        published_at: Date.now(),
        message_rich: {
          type: 'upload_error',
          ...error
        }
      })
    })

    if (files.length > MAX_FILES_UPLOAD_AT_ONCE) {
      const conciergeMessage = messages.value.find((cm) => !!cm.concierge)

      addMessage({
        published_at: Date.now(),
        concierge: conciergeMessage || {},
        message_rich: {},
        message: i18n.t('file_upload.max_files_upload_simultaneously')
      })
    }
  }

  function removeChat() {
    chat.value = null
    messages.value = []
  }

  function triggerRestaurantCode() {
    if (!restaurantStore.code) return

    retry(
      () => {
        Reservation.startRestaurantReservation(null, restaurantStore.code, chatId.value).then()
      },
      () => chat.value,
      100,
      20
    )
  }

  function sendIconAutoMessage(message) {
    if (!message) return
    if (!chat.value) {
      lazyIconAutoMessage = message
      return
    }

    if (message.type === 'icon_action') {
      sendIconMessageAction(message)
    } else if (['icon_image', 'icon_message'].includes(message.type)) {
      sendIconMessageImage(message)
    }
  }

  function sendIconMessageImage(message) {
    if (!['icon_image', 'icon_message'].includes(message.type)) return

    const { id, triggerFlow: messageAction } = message
    const params = { iconMessageID: id }

    if (messageAction !== 'no_action') {
      params.messageAction = messageAction
    }

    if (id) Chat.sendIconImageMessage(chatId.value, params)
  }

  function sendIconMessageAction(message) {
    if (!message.buttons[0]) return

    if (!['icon_action'].includes(message.type)) return

    if (['booking_widget', 'room_reservation'].includes(message.trigger_flow)) {
      Vue.prototype.$gtag?.recordIconActionRoomReservationTriggered(message)
    }

    const options = {
      messageAction: message.buttons[0].trigger_flow,
      messageActionId: message.buttons[0].trigger_flow_id,
      resetValue: true,
      useAi: true,
      autoMessageType: 'icon_action',
      iconMessageID: message.id
    }
    Chat.sendMessage(message.text, options)
  }

  async function refreshOAuthLogin(message) {
    if (!message.oauth_callback === 'success') return

    await unsubscribeChat(message.data.old_chat)
    removeChat()
    setChat(message.data.chat)
    triplaLocalStorage.setItem(
      'triplabot_known_user_signed_in',
      message.data.user.known_user_signed_in
    )
    triplaLocalStorage.setItem('triplabot_user_id', message.data.user.id)
    userStore.setUser(message.data.user)
    appStore.setMinimized(false)
    iconMessageStore.fetchCDPIconMessages()

    await fetchChats()
  }

  function changeChat(targetCode, options = {}) {
    // options: { action: (  , paramName:, paramValue:) }
    // targetCode = 'c3faa9f98e166b21b47be300bbc01257' example code

    // Handle where we are switching back to the Brand hotel.
    if (targetCode === triplaLocalStorage.getItem('original_brand_hotel_code')) {
      // When user back to brand-facility
      clearStoredBrandChat()
      clearStoredBranchChat()
      const branchId = settingsStore.isBrandFacility
        ? triplaLocalStorage.getItem('original_chat_message_id')
        : chatId.value
      if (!options.signOut) {
        brandChatbotStore.fetchBranchCount(branchId)
      }
    } else {
      // When user select facility
      storeBrandHotelOnSwitch(appStore.websiteFacilityCode)
      triplaLocalStorage.setItem('original_chat_message_id', chatId.value)
    }

    // If we are switching chats after a refresh, pubnub
    // isn't open, so we don't need to do this.
    unsubscribeChat()

    if (chat.value) {
      options.original_chat_id = chat.value.id
    }
    loading.value = true
    Chat.changeChat(targetCode, options)
      .then((json) => {
        const colors = {
          themeColor: json.theme_color,
          textColor: json.font_color
        }

        unsubscribeChat()
        removeChat()
        settingsStore.updateChatHeaders(json)
        settingsStore.updateMenuItems(json)
        colorsStore.setThemeAndTextColor(colors)
        setChat(json.new_chat)
        appStore.setImageURL(getChatIconImageFromSettings(json))

        initPubNub(json.new_chat)
        return fetchMessages(json.new_chat)
      })
      .then(() => {
        appStore.setLatestMessageID(messages.value[messages.value.length - 1].id)
        return subscribeChat(chat.value)
      })
      .then(() => {
        loading.value = false
        // trigger the non-phone auto messages when required
        triggerChatOpen()
      })
      .then(() => {
        if (triplaLocalStorage.getItem('original_brand_hotel_code')) {
          storeBranchHotelOnSwitch(targetCode)
        }
      })
      .catch((err) => {
        console.error('error', err)
      })
  }

  return {
    chat,
    messages,
    loading,
    isTyping,

    // getters
    chatId,

    setChat,
    fetchChats,
    fetchMessages,
    sendFiles,
    removeChat,
    unsubscribeChat,
    publishTyping,
    triggerRestaurantCode,
    refreshChat,
    changeChat,
    pushChatMessage,
    showAutoMessageOpen,
    sendIconAutoMessage,
    initForm
  }
})
